import moment from "moment-timezone";
import BaseDate from "./base_date";
import { userTZ, userFormat } from "hb/lib/date_helpers";

const isoFormat = "YYYY-MM-DDTHH:mm:ss";
const isoMillisecondsFormat = "YYYY-MM-DDTHH:mm:ss.SSS";

const spaceIsoFormat = "YYYY-MM-DD HH:mm:ss";
const isoMillisecondsSpaceFormat = "YYYY-MM-DD HH:mm:ss.SSS";

const libratoFormat = "MMM D, YY h:mm A"; // Sep 14, 23 9:30 AM

const availableFormats = [
  // Basic time formats with milliseconds
  "k:mm",
  "k:mm:ss",
  "k:mm:ss.SSS", // with milliseconds

  // 12-hour formats with AM/PM
  "h:mma",
  "h:mm a",
  "h:mm:ssa", // with seconds
  "h:mm:ss a", // with seconds
  "h:mm:ss.SSSa", // with milliseconds
  "h:mm:ss.SSS a", // with milliseconds
  "ha",

  // Day and time formats
  "D k:mm",
  "D k:mm:ss",
  "D k:mm:ss.SSS", // with milliseconds

  "D h:mma",
  "D h:mm a",
  "D h:mm:ssa", // with seconds
  "D h:mm:ss a", // with seconds
  "D h:mm:ss.SSSa", // with milliseconds
  "D h:mm:ss.SSS a", // with milliseconds
  "D ha",

  // Month, day and time formats
  "M.D k:mm",
  "M.D k:mm:ss",
  "M.D k:mm:ss.SSS", // with milliseconds

  "M.D h:mma",
  "M.D h:mm a",
  "M.D h:mm:ssa", // with seconds
  "M.D h:mm:ss a", // with seconds
  "M.D h:mm:ss.SSSa", // with milliseconds
  "M.D h:mm:ss.SSS a", // with milliseconds
  "M.D ha",

  // Date-only formats
  "YYYY",
  "YYYY-MM",
  "YYYY-MM-DD",
  "MM/DD/YY",
  "M/DD/YY",
  "MM/DD",
  "M/D",
  "M.D",
  "M.D.Y",

  // Date-time formats with milliseconds
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm:ss.SSS", // with milliseconds

  // Maintaining the original special formats
  spaceIsoFormat,
  isoMillisecondsSpaceFormat,
  libratoFormat,
  isoFormat,
  isoMillisecondsFormat,

  // Adding ISO formats with milliseconds
  "YYYY-MM-DDTHH:mm:ss.SSS", // ISO with milliseconds
];

export const DAY_INT = 86400000;

export default class Temporal extends BaseDate {
  constructor(date) {
    super();

    this.date = date;
    this.outputFormat = userFormat(true);
  }

  dup() {
    return new Temporal(this.date.clone());
  }

  static fromTimestamp(timestamp) {
    return new Temporal(moment(timestamp).utc());
  }

  static fromInput(str) {
    return new Temporal(
      moment(str, [...availableFormats, userFormat(true)], true),
    );
  }

  static fromParam(str) {
    return new Temporal(moment(str, [isoFormat, isoMillisecondsFormat], true));
  }

  static timezoneAbbreviation(timezone) {
    return moment().tz(timezone).format("z");
  }

  static localTimezone() {
    const zone = userTZ();
    return { zone, abbr: this.timezoneAbbreviation(zone) };
  }

  static convertFromTz(time, timezone) {
    return moment
      .tz(time, isoMillisecondsFormat, timezone)
      .utc()
      .format(isoMillisecondsFormat);
  }

  static now() {
    return new Temporal(moment());
  }

  inLocalTimezone() {
    this.date.tz(userTZ());
    return this;
  }

  valid() {
    return this.date.isValid();
  }

  hasMilliseconds() {
    return this.date.millisecond() !== 0;
  }

  toIso8601() {
    if (this.hasMilliseconds()) {
      return this.date.format(isoMillisecondsFormat);
    } else {
      return this.date.format(isoFormat);
    }
  }

  get output() {
    if (this.hasMilliseconds()) {
      return this.date.format(isoMillisecondsSpaceFormat);
    } else {
      return this.date.format(this.outputFormat);
    }
  }

  get param() {
    return this.toIso8601();
  }
}
